import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Download } from 'lucide-react';
import UserMenu from '@/components/UserMenu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Half-court dimensions (scaled to 400x380 SVG)
const COURT_W = 400;
const COURT_H = 380;

function ShotChart({ shots, teamFilter, game }) {
  const filtered = teamFilter === 'all' ? shots : shots.filter(s => s.team === teamFilter);

  // Distribute shots pseudo-randomly on a half-court based on type
  const plotShots = filtered.map((shot, i) => {
    const seed = i * 2654435761;
    const rand1 = ((seed >>> 0) % 1000) / 1000;
    const rand2 = (((seed * 7) >>> 0) % 1000) / 1000;

    let x, y;
    if (shot.type === '3pt') {
      // Arc positions for 3-pointers
      const angle = rand1 * Math.PI;
      const radius = 150 + rand2 * 30;
      x = COURT_W / 2 + Math.cos(angle) * radius;
      y = 60 + Math.sin(angle) * (radius * 0.7);
    } else {
      // Inside the paint area
      x = COURT_W / 2 + (rand1 - 0.5) * 140;
      y = 40 + rand2 * 160;
    }
    // Clamp to court bounds
    x = Math.max(20, Math.min(COURT_W - 20, x));
    y = Math.max(20, Math.min(COURT_H - 30, y));
    return { ...shot, x, y };
  });

  return (
    <div className="bg-[#C67A3B] rounded-lg p-2 inline-block" data-testid="shot-chart">
      <svg viewBox={`0 0 ${COURT_W} ${COURT_H}`} className="w-full max-w-[500px]">
        {/* Court lines */}
        <rect x="10" y="10" width={COURT_W - 20} height={COURT_H - 20} fill="none" stroke="#fff" strokeWidth="2" rx="4" />
        {/* Free throw lane */}
        <rect x={COURT_W / 2 - 60} y="10" width="120" height="150" fill="none" stroke="#fff" strokeWidth="1.5" />
        {/* Free throw circle */}
        <circle cx={COURT_W / 2} cy="160" r="45" fill="none" stroke="#fff" strokeWidth="1.5" strokeDasharray="6 4" />
        {/* Basket */}
        <circle cx={COURT_W / 2} cy="40" r="8" fill="none" stroke="#fff" strokeWidth="2" />
        <line x1={COURT_W / 2 - 20} y1="10" x2={COURT_W / 2 + 20} y2="10" stroke="#fff" strokeWidth="3" />
        {/* 3-point arc */}
        <path d={`M 40 10 Q 40 300 ${COURT_W / 2} 310 Q ${COURT_W - 40} 300 ${COURT_W - 40} 10`}
          fill="none" stroke="#fff" strokeWidth="1.5" />
        {/* Half court line */}
        <line x1="10" y1={COURT_H - 10} x2={COURT_W - 10} y2={COURT_H - 10} stroke="#fff" strokeWidth="2" />

        {/* Shots */}
        {plotShots.map((shot, i) => (
          <g key={i}>
            {shot.made ? (
              <circle cx={shot.x} cy={shot.y} r="6" fill={shot.type === '3pt' ? '#22c55e' : '#3b82f6'} stroke="#fff" strokeWidth="1.5" opacity="0.85" />
            ) : (
              <g>
                <line x1={shot.x - 4} y1={shot.y - 4} x2={shot.x + 4} y2={shot.y + 4} stroke="#ef4444" strokeWidth="2" />
                <line x1={shot.x + 4} y1={shot.y - 4} x2={shot.x - 4} y2={shot.y + 4} stroke="#ef4444" strokeWidth="2" />
              </g>
            )}
          </g>
        ))}
      </svg>
      <div className="flex items-center justify-center gap-4 mt-2 text-xs text-white/80">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-blue-500 inline-block"></span> 2PT Made</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500 inline-block"></span> 3PT Made</span>
        <span className="flex items-center gap-1 text-red-400">&#10005; Miss</span>
      </div>
    </div>
  );
}

function GameFlowChart({ scoreFlow, game }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || scoreFlow.length === 0) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    const pad = { top: 30, bottom: 30, left: 40, right: 20 };

    ctx.clearRect(0, 0, W, H);

    const maxScore = Math.max(
      ...scoreFlow.map(s => Math.max(s.score_a, s.score_b)),
      10
    );

    const plotW = W - pad.left - pad.right;
    const plotH = H - pad.top - pad.bottom;

    // Background
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, W, H);

    // Grid lines
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 1;
    const gridSteps = 5;
    for (let i = 0; i <= gridSteps; i++) {
      const y = pad.top + (plotH / gridSteps) * i;
      ctx.beginPath();
      ctx.moveTo(pad.left, y);
      ctx.lineTo(W - pad.right, y);
      ctx.stroke();
      // Label
      ctx.fillStyle = '#94a3b8';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(Math.round(maxScore - (maxScore / gridSteps) * i), pad.left - 6, y + 4);
    }

    // Draw lines
    function drawLine(data, key, color) {
      ctx.strokeStyle = color;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      // Start at 0
      ctx.moveTo(pad.left, pad.top + plotH);
      data.forEach((point, i) => {
        const x = pad.left + ((i + 1) / data.length) * plotW;
        const y = pad.top + plotH - (point[key] / maxScore) * plotH;
        ctx.lineTo(x, y);
      });
      ctx.stroke();
    }

    drawLine(scoreFlow, 'score_a', '#00247D');
    drawLine(scoreFlow, 'score_b', '#CF142B');

    // Legend
    ctx.font = 'bold 11px sans-serif';
    ctx.fillStyle = '#00247D';
    ctx.textAlign = 'left';
    ctx.fillText(game.team_a.name, pad.left, 16);
    ctx.fillStyle = '#CF142B';
    ctx.fillText(game.team_b.name, pad.left + 120, 16);

  }, [scoreFlow, game]);

  return (
    <canvas ref={canvasRef} width={600} height={300} className="w-full max-w-[600px] rounded-lg border border-slate-200" data-testid="game-flow-chart" />
  );
}

function QuarterBreakdown({ quarterScores, game }) {
  const quarters = Object.keys(quarterScores).sort((a, b) => Number(a) - Number(b));
  return (
    <div className="overflow-x-auto" data-testid="quarter-breakdown">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="px-3 py-2 text-left text-xs font-bold text-slate-500">Team</th>
            {quarters.map(q => (
              <th key={q} className="px-3 py-2 text-center text-xs font-bold text-slate-500">{Number(q) <= 4 ? `Q${q}` : 'OT'}</th>
            ))}
            <th className="px-3 py-2 text-center text-xs font-bold text-slate-500">Total</th>
          </tr>
        </thead>
        <tbody>
          {['team_a', 'team_b'].map(team => {
            const total = quarters.reduce((s, q) => s + (quarterScores[q]?.[team] || 0), 0);
            return (
              <tr key={team} className="border-b border-slate-100">
                <td className="px-3 py-2 font-semibold">{game[team].name}</td>
                {quarters.map(q => (
                  <td key={q} className="px-3 py-2 text-center">{quarterScores[q]?.[team] || 0}</td>
                ))}
                <td className="px-3 py-2 text-center font-bold text-[#00247D]">{total}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function GameFlow() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shotTeam, setShotTeam] = useState('all');

  // Preserve the public-share back-chain: when the visitor opened analytics
  // from a /share/{token} → /stats/summary/{id}?share={token} link, the
  // Analytics back arrow returns them to the same Game Summary with the
  // share token still attached so subsequent back-navigation lands on
  // /share/{token} (not the auth-only /stats hub).
  const shareToken = searchParams.get('share') || '';
  const backHref = shareToken
    ? `/stats/summary/${gameId}?share=${shareToken}`
    : `/stats/summary/${gameId}`;

  useEffect(() => {
    fetchFlow();
  }, [gameId]);

  const fetchFlow = async () => {
    try {
      const res = await fetch(`${API_URL}/api/stats/games/${gameId}/game-flow`);
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleExport = () => {
    window.open(`${API_URL}/api/stats/games/${gameId}/export-csv`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#00247D] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return <div className="p-8 text-center">Game not found</div>;
  const { game, score_flow, quarter_scores, shots, final_score } = data;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="glass-effect sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(backHref)} data-testid="game-flow-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="text-xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>GAME ANALYTICS</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleExport} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
            <UserMenu inline={true} />
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Score header */}
        <div className="text-center">
          <p className="text-xs text-slate-500 uppercase mb-1">{game.name} &middot; {game.date}</p>
          <div className="flex items-center justify-center gap-4">
            <span className="text-sm font-bold text-slate-700">{game.team_a.name}</span>
            <span className="text-3xl font-black text-[#00247D]">{final_score.team_a}</span>
            <span className="text-slate-400">-</span>
            <span className="text-3xl font-black text-[#CF142B]">{final_score.team_b}</span>
            <span className="text-sm font-bold text-slate-700">{game.team_b.name}</span>
          </div>
        </div>

        {/* Video */}
        {game.video_url && (
          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-bold text-[#00247D] uppercase mb-3" style={{ fontFamily: 'Barlow Condensed' }}>Game Video</h3>
              {game.video_url.includes('youtube.com') || game.video_url.includes('youtu.be') ? (
                <div className="aspect-video rounded-lg overflow-hidden">
                  <iframe
                    src={game.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                    className="w-full h-full"
                    allowFullScreen
                    title="Game Video"
                  />
                </div>
              ) : (
                <a href={game.video_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#00247D] hover:underline text-sm" data-testid="video-link">
                  Watch Game Video
                </a>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quarter Breakdown */}
        {Object.keys(quarter_scores).length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-bold text-[#00247D] uppercase mb-3" style={{ fontFamily: 'Barlow Condensed' }}>Quarter Breakdown</h3>
              <QuarterBreakdown quarterScores={quarter_scores} game={game} />
            </CardContent>
          </Card>
        )}

        {/* Game Flow */}
        {score_flow.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-bold text-[#00247D] uppercase mb-3" style={{ fontFamily: 'Barlow Condensed' }}>Game Flow</h3>
              <GameFlowChart scoreFlow={score_flow} game={game} />
            </CardContent>
          </Card>
        )}

        {/* Shot Chart */}
        {shots.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-[#00247D] uppercase" style={{ fontFamily: 'Barlow Condensed' }}>Shot Chart</h3>
                <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'a', label: game.team_a.name },
                    { key: 'b', label: game.team_b.name }
                  ].map(f => (
                    <button key={f.key}
                      className={`px-3 py-1 text-xs font-bold rounded transition-colors ${shotTeam === f.key ? 'bg-white shadow text-[#00247D]' : 'text-slate-500'}`}
                      onClick={() => setShotTeam(f.key)}>
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex justify-center">
                <ShotChart shots={shots} teamFilter={shotTeam} game={game} />
              </div>
              <div className="mt-3 text-center text-xs text-slate-500">
                {shots.filter(s => shotTeam === 'all' || s.team === shotTeam).filter(s => s.made).length} /
                {' '}{shots.filter(s => shotTeam === 'all' || s.team === shotTeam).length} shots made
                ({(() => { const total = shots.filter(s => shotTeam === 'all' || s.team === shotTeam).length; return total > 0 ? Math.round(shots.filter(s => (shotTeam === 'all' || s.team === shotTeam) && s.made).length / total * 100) : 0; })()}%)
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
